import React, { useEffect, useState } from 'react';
import { Backdrop, Box, CircularProgress, useTheme } from '@mui/material';
import {
    ArticleTarifDTOJsonldDonneesFacturationRead,
    DossierTarifDTOJsonldDonneesFacturationRead,
} from '@europrocurement/l2d-domain/openApi/ApiFacturation';
import { REALM_DEBUG, UseKeycloakCheckRole } from '@europrocurement/l2d-keycloak';
import {
    CollectionDeDossierCollectiondossierUpdate,
    CollectionDeDossierJsonldCollectiondossierRead,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { FactureFormaliteApiObject, useFormaliteService } from '@europrocurement/l2d-domain';
import { useToaster } from '@europrocurement/l2d-modules/hooks';

import { DossiersArticlesReductions } from '../../../hooks/useDossierPrefacturation';
import { useBilling } from '../../../hooks';
import { CollectionProvisionType } from '../../../hooks/useCollectionProvision';
import {
    facturationAgregee,
    facturationAgregeeDescription,
    facturationAgregeeLibelle,
    facturationComplete,
    facturationCompleteDescription,
    facturationCompleteLibelle,
    facturationSimplifiee,
    facturationSimplifieeDescription,
    facturationSimplifieeLibelle,
    facturationType,
    heightFooterFacturation,
} from './functions';
import OrderDetailsSection from './components/OrderDetailsSection/OrderDetailsSection';
import EncaissementsSection from './components/EncaissementsSection/EncaissementsSection';
import ProvisionsSection from './components/ProvisionsSection/ProvisionsSection';
import TotalsSection from './components/TotalsSection/TotalsSection';

export type DossierInformationsProps = {
    reloadInvoiceEstimates: () => void;
    dataPrefacturation: DossiersArticlesReductions | null;
    acomptes: Array<CollectionProvisionType>;
    reglements: Array<CollectionProvisionType>;
    collections: CollectionDeDossierJsonldCollectiondossierRead[];
    sortableListOnChange: (
        items: (Omit<ArticleTarifDTOJsonldDonneesFacturationRead, 'id'> & {
            id: number;
        })[],
        dossier: DossierTarifDTOJsonldDonneesFacturationRead,
        dossierIdx: number,
    ) => void;
    ajustInvoice: () => Promise<void>;
    afterSubmit: () => void;
    afterSubmitAcompte: () => void;
    afterSubmitInvoice: () => void;
    afterSubmitAcompteAttendu: () => void;
    onChangeDossiersSelected?: (id: number, checked: boolean) => void;
    dossiersSelected?: Array<number>;
    onload?: boolean;
    onClickDemandeReglement: (type: string) => Promise<void>;
    onClickPaiement: (amount: number, ids: number[], type: 'collection' | 'facture') => void;
    handleValidateInvoice: (invoice: FactureFormaliteApiObject) => Promise<void>;
    activeInvoice: number;
};
const DossierFacturation: React.FunctionComponent<DossierInformationsProps> = function ({
    reloadInvoiceEstimates,
    dataPrefacturation,
    acomptes,
    reglements,
    collections,
    sortableListOnChange,
    ajustInvoice,
    afterSubmit,
    afterSubmitAcompte,
    afterSubmitInvoice,
    afterSubmitAcompteAttendu,
    onChangeDossiersSelected,
    dossiersSelected,
    onload,
    onClickDemandeReglement,
    onClickPaiement,
    handleValidateInvoice,
    activeInvoice,
}) {
    const roleChecker = UseKeycloakCheckRole();
    const isTech = roleChecker(REALM_DEBUG);

    const { palette } = useTheme();

    const [remisesInvisiblesAffichees, setRemisesInvisiblesAffichees] =
        React.useState<boolean>(false);

    const [typeFacturation, setTypeFacturation] =
        React.useState<facturationType>(facturationComplete);
    const [acompteAttendu, setAcompteAttendu] = React.useState<number>(0);
    const [collectionsId, setCollectionsId] = React.useState<number[]>([]);

    useEffect(() => {
        if (collections.length > 0) {
            if (collections[0].typeFacturation) {
                setTypeFacturation(collections[0].typeFacturation);
            } else {
                setTypeFacturation(facturationComplete);
            }
            setAcompteAttendu(
                parseFloat(
                    collections
                        .reduce(
                            (total, collection) =>
                                total +
                                (collection.acompteAttendu
                                    ? parseFloat(collection.acompteAttendu)
                                    : 0),
                            0,
                        )
                        .toFixed(2),
                ),
            );

            setCollectionsId(
                collections
                    .map((collection) => {
                        if (!collection.id) return undefined;
                        return collection.id;
                    })
                    .filter((id) => id !== undefined),
            );
        }
    }, [collections]);

    const { collectionApi } = useFormaliteService();
    const { handleApiResponse } = useToaster();

    const [loading, setLoading] = useState<boolean>(false);

    const totalAcomptes = parseFloat(
        acomptes
            .reduce(
                (total, acompte) => total + (acompte.montant ? parseFloat(acompte.montant) : 0),
                0,
            )
            .toFixed(2),
    );

    const totalReglements = parseFloat(
        reglements
            .reduce(
                (total, reglement) =>
                    total + (reglement.montant ? parseFloat(reglement.montant) : 0),
                0,
            )
            .toFixed(2),
    );

    const { createInvoice, createEstimates, createProforma } = useBilling({ origine: 'B2D' });

    const updateFacturationType = async (newTypeFacturation: facturationType) => {
        setLoading(true);
        const collectionDeDossierCollectiondossierUpdate: CollectionDeDossierCollectiondossierUpdate =
            {
                typeFacturation: newTypeFacturation,
            };

        const createRequest = collectionApi.apiCollectionsDossiersIdPatch({
            // TODO LLC 1876 changer pour une route avec plusieurs id collections pour toutes les changer en une fois
            id: collections[0].id as unknown as string,
            collectionDeDossierCollectiondossierUpdate,
        });

        await handleApiResponse(createRequest, () => {
            setLoading(false);
            afterSubmit();
        });
    };

    const typesFacturation = [
        {
            label: facturationCompleteLibelle,
            value: facturationComplete,
            description: facturationCompleteDescription,
        },
        {
            label: facturationSimplifieeLibelle,
            value: facturationSimplifiee,
            description: facturationSimplifieeDescription,
        },
        {
            label: facturationAgregeeLibelle,
            value: facturationAgregee,
            description: facturationAgregeeDescription,
        },
    ];
    const filteredPrefacturation = {
        ...(dataPrefacturation || {}),
        dossiers:
            dataPrefacturation?.dossiers?.filter(
                (dossier) => dossier.id !== undefined && dossiersSelected?.includes(dossier.id),
            ) ?? [],
    };

    return dataPrefacturation && dataPrefacturation.dossiers && collections && !onload ? (
        <Box
            sx={{
                marginBottom: heightFooterFacturation,
            }}
        >
            <Backdrop
                sx={(backDropTheme) => ({ color: '#fff', zIndex: backDropTheme.zIndex.drawer + 1 })}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            <OrderDetailsSection
                isTech={isTech}
                remisesInvisiblesAffichees={remisesInvisiblesAffichees}
                setRemisesInvisiblesAffichees={setRemisesInvisiblesAffichees}
                collections={collections}
                dataPrefacturation={dataPrefacturation}
                dataPrefacturationDossiers={dataPrefacturation.dossiers}
                palette={palette}
                afterSubmit={afterSubmit}
                sortableListOnChange={sortableListOnChange}
                onChangeDossiersSelected={onChangeDossiersSelected}
                dossiersSelected={dossiersSelected}
            />

            <ProvisionsSection
                acompteAttendu={acompteAttendu}
                totalAcomptes={totalAcomptes}
                totalReglements={totalReglements}
                collections={collections}
                afterSubmitAcompte={afterSubmitAcompte}
                afterSubmitAcompteAttendu={afterSubmitAcompteAttendu}
                onClickDemandeReglement={onClickDemandeReglement}
                onClickPaiement={onClickPaiement}
                palette={palette}
                activeInvoice={activeInvoice}
            />

            <EncaissementsSection
                collections={collections}
                totalAcomptes={totalAcomptes}
                totalReglements={totalReglements}
                afterSubmitAcompte={afterSubmitAcompte}
                reglements={reglements}
                acomptes={acomptes}
                handleValidateInvoice={handleValidateInvoice}
            />

            <TotalsSection
                collectionsId={collectionsId}
                totalAcomptes={totalAcomptes}
                acomptes={acomptes}
                typeFacturation={typeFacturation}
                typesFacturation={typesFacturation}
                dataPrefacturation={filteredPrefacturation}
                ajustInvoice={ajustInvoice}
                afterSubmitInvoice={afterSubmitInvoice}
                reloadInvoiceEstimates={reloadInvoiceEstimates}
                updateFacturationType={updateFacturationType}
                createEstimates={createEstimates}
                createInvoice={createInvoice}
                createProforma={createProforma}
                setLoading={setLoading}
                setTypeFacturation={setTypeFacturation}
            />
        </Box>
    ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
            <CircularProgress />
        </Box>
    );
};

export default DossierFacturation;
